import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { authService } from '@/services/auth.service';
import { clientApiService } from '@/services/api/client.service';
import type { 
  AuthContextValue, 
  AuthState, 
  LoginData, 
  ProfileUpdateData 
} from '@/types/auth.types';
import type { User } from '@/types/user';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isInitialized: false,
  error: null,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Helper function to set Sentry user context
const setSentryUser = (user: User | null) => {
  if (import.meta.env.VITE_ENVIRONMENT === 'production' && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(user ? {
      id: user.id,
      email: user.email,
      username: user.name,
      role: user.role,
    } : null);
  }
};

// Helper function to preload client details for faster routing
const preloadClientDetails = async (user: User) => {
  if (user.role === 'client' && user.client_project_id) {
    try {
      console.log('AuthContext: Preloading client details for faster routing');
      // This will cache the client details for useClientBotType hook
      await clientApiService.getClientDetails(user.client_project_id);
      console.log('AuthContext: Client details preloaded successfully');
    } catch (error) {
      console.warn('AuthContext: Failed to preload client details:', error);
      // Don't throw - this is just preloading for performance
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const navigate = useNavigate();

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      // Check if we have a token
      const token = authService.getAccessToken();
      
      if (token && !authService.isTokenExpired()) {
        // Try to get user profile
        try {
          const user = await authService.getProfile();
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
          
          // Set Sentry user context for error tracking
          setSentryUser(user);
          
          // Preload client details for faster routing (don't await - run in background)
          preloadClientDetails(user);
        } catch (error) {
          // Token might be invalid, clear it
          authService.clearTokens();
          setState({
            ...initialState,
            isLoading: false,
            isInitialized: true,
          });
        }
      } else {
        // No valid token
        authService.clearTokens();
        setState({
          ...initialState,
          isLoading: false,
          isInitialized: true,
        });
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      setState({
        ...initialState,
        isLoading: false,
        isInitialized: true,
        error: 'Failed to initialize authentication',
      });
    }
  };

  const login = useCallback(async (data: LoginData) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await authService.login(data);
      
      if (response.success && response.user) {
        setState({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
        
        // Set Sentry user context for error tracking
        setSentryUser(response.user);
        
        // Preload client details for faster routing (don't await - run in background)
        preloadClientDetails(response.user);
        
        // Redirect to dashboard - RoleBasedRoutes will handle the appropriate view
        navigate('/dashboard');
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed',
      }));
      throw error;
    }
  }, [navigate]);


  const logout = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      await authService.logout();
      
      setState({
        ...initialState,
        isLoading: false,
        isInitialized: true,
      });
      
      // Clear Sentry user context
      setSentryUser(null);
      
      navigate('/auth');
    } catch (error: any) {
      console.error('Logout error:', error);
      // Still clear local state even if logout fails
      setState({
        ...initialState,
        isLoading: false,
        isInitialized: true,
      });
      
      // Clear Sentry user context
      setSentryUser(null);
      
      navigate('/auth');
    }
  }, [navigate]);

  const updateProfile = useCallback(async (data: ProfileUpdateData) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const updatedUser = await authService.updateProfile(data);
      
      setState(prev => ({
        ...prev,
        user: updatedUser,
        isLoading: false,
        error: null,
      }));
      
      // Profile update successful, no redirect needed
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to update profile',
      }));
      throw error;
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const user = await authService.getProfile();
      setState(prev => ({
        ...prev,
        user,
        error: null,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to refresh authentication',
      }));
      throw error;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = authService.getRefreshToken();
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }
      
      await authService.refreshToken(refreshTokenValue);
      
      // After successful token refresh, update the user data
      await refreshAuth();
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to refresh token',
      }));
      throw error;
    }
  }, [refreshAuth]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    updateProfile,
    refreshAuth,
    refreshToken,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};